/**
 * Billing hooks using React Query
 * Provides data fetching and mutations for billing features
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '@/services/billing';
import type {
  Plan,
  SubscriptionResponse,
  Entitlement,
  CheckoutSession,
  CreateCheckoutRequest,
  CouponValidation,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  PaymentHistoryResponse,
  TrialStatus,
} from '@/types/billing';

// Query keys for cache management
export const billingKeys = {
  all: ['billing'] as const,
  plans: () => [...billingKeys.all, 'plans'] as const,
  plan: (id: string) => [...billingKeys.plans(), id] as const,
  subscription: () => [...billingKeys.all, 'subscription'] as const,
  entitlements: () => [...billingKeys.all, 'entitlements'] as const,
  payments: () => [...billingKeys.all, 'payments'] as const,
  trialStatus: () => [...billingKeys.all, 'trialStatus'] as const,
};

// ============================================================================
// Plan Hooks
// ============================================================================

/**
 * Hook to fetch all available plans
 */
export function usePlans() {
  return useQuery({
    queryKey: billingKeys.plans(),
    queryFn: () => billingApi.getPlans(),
    staleTime: 10 * 60 * 1000, // Plans don't change often, cache for 10 minutes
  });
}

/**
 * Hook to fetch a single plan
 */
export function usePlan(planId: string) {
  return useQuery({
    queryKey: billingKeys.plan(planId),
    queryFn: () => billingApi.getPlan(planId),
    enabled: !!planId,
    staleTime: 10 * 60 * 1000,
  });
}

// ============================================================================
// Subscription Hooks
// ============================================================================

/**
 * Hook to fetch current subscription
 */
export function useSubscription() {
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => billingApi.getSubscription(),
    staleTime: 60 * 1000, // Cache for 1 minute
  });
}

/**
 * Hook to create a checkout session
 * Returns a mutation that creates a Stripe Checkout session
 */
export function useCreateCheckout() {
  return useMutation<CheckoutSession, Error, CreateCheckoutRequest>({
    mutationFn: (request) => billingApi.createCheckoutSession(request),
  });
}

/**
 * WP-BILLING-19 Part B: change plan in place. Entitlements change at once, so
 * both the subscription and the entitlements are refetched.
 */
export function useChangePlan() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (planId) => billingApi.changePlan(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: billingKeys.entitlements() });
    },
  });
}

/**
 * Hook to cancel subscription
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation<CancelSubscriptionResponse, Error, CancelSubscriptionRequest>({
    mutationFn: (request) => billingApi.cancelSubscription(request),
    onSuccess: () => {
      // Invalidate subscription data to refetch
      queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: billingKeys.entitlements() });
    },
  });
}

/**
 * Hook to resume a canceled subscription
 */
export function useResumeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => billingApi.resumeSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: billingKeys.entitlements() });
    },
  });
}

// ============================================================================
// Entitlements Hooks
// ============================================================================

/**
 * Hook to fetch tenant's active entitlements
 */
export function useEntitlements() {
  return useQuery({
    queryKey: billingKeys.entitlements(),
    queryFn: () => billingApi.getEntitlements(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to check if a specific feature is entitled
 */
export function useHasEntitlement(featureKey: string) {
  const { data: entitlements, isLoading, error } = useEntitlements();

  const hasEntitlement = entitlements?.some(
    (e: Entitlement) => e.featureKey === featureKey && e.isActive
  ) ?? false;

  return {
    hasEntitlement,
    isLoading,
    error,
  };
}

// ============================================================================
// Coupon Hooks
// ============================================================================

/**
 * Hook to validate a coupon code
 * Returns a mutation that validates the coupon
 */
export function useValidateCoupon() {
  return useMutation<CouponValidation, Error, string>({
    mutationFn: (code) => billingApi.validateCoupon(code),
  });
}

// ============================================================================
// Payment History Hooks
// ============================================================================

/**
 * Hook to fetch payment history
 */
export function usePaymentHistory(cursor?: string, limit: number = 10) {
  return useQuery({
    queryKey: [...billingKeys.payments(), { cursor, limit }],
    queryFn: () => billingApi.getPaymentHistory(cursor, limit),
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

// ============================================================================
// Billing Portal Hook
// ============================================================================

/**
 * Hook to get and redirect to Stripe billing portal
 */
export function useBillingPortal() {
  return useMutation({
    mutationFn: (returnUrl?: string) => billingApi.getPortalUrl(returnUrl),
  });
}

// ============================================================================
// Trial Status Hooks
// ============================================================================

/**
 * Hook to fetch trial status for current tenant
 */
export function useTrialStatus() {
  return useQuery<TrialStatus>({
    queryKey: billingKeys.trialStatus(),
    queryFn: () => billingApi.getTrialStatus(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 3, // Retry up to 3 times on failure (increased from 1)
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
  });
}

// ============================================================================
// Utility Types
// ============================================================================

export type { Plan, SubscriptionResponse, Entitlement, CouponValidation, PaymentHistoryResponse, TrialStatus };
